import { supabase, TABLES, STORAGE_BUCKETS } from "../config/supabase";
import { Article } from "./articleService";
import { articles as hardcodedArticles } from "../constants/articles";

export interface DatabaseArticle {
  id: number;
  slug: string;
  title_key: string;
  meta_key: string;
  content_title_key: string;
  content_key: string;
  tags_key: string;
  image_url: string;
  image_details_url: string;
  created_at: string;
  updated_at: string;
}

export interface NewDatabaseArticle {
  slug: string;
  article_key: string;
  image_url: string;
  image_details_url: string;
  title_key?: string;
  meta_key?: string;
  content_title_key?: string;
  content_key?: string;
  tags_key?: string;
}

export interface ArticleWithUrls extends Article {
  image_url?: string;
  image_details_url?: string;
}

class SupabaseService {
  // Compute next article key like article9
  async getNextArticleKey(): Promise<string> {
    try {
      // Get all existing article keys from Supabase
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .select("article_key");
      if (error) throw error;

      // Get hardcoded article keys from articles.ts
      const hardcodedKeys = hardcodedArticles
        .map((article) => {
          // Extract article key from title_key like "articles.article1.title"
          const match = article.titleKey.match(/^articles\.([^.]+)\./);
          return match ? match[1] : null;
        })
        .filter(Boolean);

      // Combine all existing keys
      const existingKeys = new Set([
        ...hardcodedKeys,
        ...(data || []).map((item) => item.article_key).filter(Boolean),
      ]);

      // Find the next available number
      let nextNum = 1;
      while (existingKeys.has(`article${nextNum}`)) {
        nextNum++;
      }

      return `article${nextNum}`;
    } catch {
      // Fallback: start from article9 (after hardcoded articles)
      return "article9";
    }
  }

  async upsertI18nRow(
    lang: "en" | "ru",
    key: string,
    value: any,
    namespace = "articles"
  ): Promise<void> {
    const { error } = await supabase
      .from("i18n")
      .upsert(
        { lang, namespace, key, value },
        { onConflict: "lang,namespace,key" }
      );
    if (error) throw error;
  }

  async upsertTranslations(
    articleKey: string,
    payload: {
      en?: {
        title?: any;
        meta?: any;
        contentTitle?: any;
        content?: any;
        tags?: any;
      };
      ru?: {
        title?: any;
        meta?: any;
        contentTitle?: any;
        content?: any;
        tags?: any;
      };
    }
  ): Promise<void> {
    const tasks: Promise<void>[] = [];
    const push = (lang: "en" | "ru", field: string, value: any) => {
      if (value !== undefined)
        tasks.push(this.upsertI18nRow(lang, `${articleKey}.${field}`, value));
    };
    if (payload.en) {
      const e = payload.en;
      push("en", "title", e.title);
      push("en", "meta", e.meta);
      push("en", "contentTitle", e.contentTitle);
      push("en", "content", e.content);
      push("en", "tags", e.tags);
    }
    if (payload.ru) {
      const r = payload.ru;
      push("ru", "title", r.title);
      push("ru", "meta", r.meta);
      push("ru", "contentTitle", r.contentTitle);
      push("ru", "content", r.content);
      push("ru", "tags", r.tags);
    }
    await Promise.all(tasks);
  }

  async fetchTranslations(articleKey: string): Promise<{
    en?: Record<string, any>;
    ru?: Record<string, any>;
  }> {
    const result: { en?: any; ru?: any } = {};
    const { data, error } = await supabase
      .from("i18n")
      .select("lang,key,value")
      .eq("namespace", "articles")
      .like("key", `${articleKey}.%`);
    if (error) return result;
    for (const row of data || []) {
      const lang = row.lang as "en" | "ru";
      const shortKey = String(row.key).split(".").slice(1).join(".");
      if (!result[lang]) result[lang] = {};
      result[lang][shortKey] = row.value;
    }
    return result;
  }

  // Article Management
  async getArticles(): Promise<Article[]> {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .select("*")
        .order("sort_index", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching articles:", error);
        return [];
      }

      // Load translations for each article
      const articlesWithTranslations = await Promise.all(
        (data || []).map(async (articleData) => {
          const article = this.mapDatabaseToArticle(articleData);

          // Load translations if article_key exists
          if (article.article_key) {
            try {
              const translations = await this.fetchTranslations(
                article.article_key
              );
              if (translations.en || translations.ru) {
                article.content = {
                  en: {
                    title: translations.en?.title || "",
                    meta: translations.en?.meta || "",
                    contentTitle: translations.en?.contentTitle || "",
                    content: translations.en?.content || "",
                    tags: translations.en?.tags || "",
                  },
                  ru: {
                    title: translations.ru?.title || "",
                    meta: translations.ru?.meta || "",
                    contentTitle: translations.ru?.contentTitle || "",
                    content: translations.ru?.content || "",
                    tags: translations.ru?.tags || "",
                  },
                };
              }
            } catch (translationError) {
              console.error(
                `Error loading translations for ${article.article_key}:`,
                translationError
              );
            }
          }

          return article;
        })
      );

      return articlesWithTranslations;
    } catch (error) {
      console.error("Error in getArticles:", error);
      return [];
    }
  }

  async getArticleBySlug(slug: string): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) {
        console.error("Error fetching article:", error);
        return null;
      }

      return data ? this.mapDatabaseToArticle(data) : null;
    } catch (error) {
      console.error("Error in getArticleBySlug:", error);
      return null;
    }
  }

  async createArticle(article: Article): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .insert([this.mapArticleToDatabase(article)])
        .select()
        .single();

      if (error) {
        console.error("Error creating article:", error);
        return null;
      }

      return data ? this.mapDatabaseToArticle(data) : null;
    } catch (error) {
      console.error("Error in createArticle:", error);
      return null;
    }
  }

  async createArticleWithTranslations(
    articleData: NewDatabaseArticle
  ): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .insert([articleData])
        .select()
        .single();

      if (error) {
        console.error("Error creating article:", error);
        return null;
      }

      return data ? this.mapDatabaseToArticle(data) : null;
    } catch (error) {
      console.error("Error in createArticleWithTranslations:", error);
      return null;
    }
  }

  async updateArticle(article: Article): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .update(this.mapArticleToDatabase(article))
        .eq("slug", article.slug)
        .select()
        .single();

      if (error) {
        console.error("Error updating article:", error);
        return null;
      }

      return data ? this.mapDatabaseToArticle(data) : null;
    } catch (error) {
      console.error("Error in updateArticle:", error);
      return null;
    }
  }

  async updateArticleBasic(articleData: {
    slug: string;
    image_url: string;
    image_details_url: string;
  }): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from(TABLES.ARTICLES)
        .update(articleData)
        .eq("slug", articleData.slug)
        .select()
        .single();

      if (error) {
        console.error("Error updating article:", error);
        return null;
      }

      return data ? this.mapDatabaseToArticle(data) : null;
    } catch (error) {
      console.error("Error in updateArticleBasic:", error);
      return null;
    }
  }

  async deleteArticle(slug: string): Promise<boolean> {
    try {
      // Fetch article first to know article_key and image URLs
      const { data: articleData, error: fetchError } = await supabase
        .from(TABLES.ARTICLES)
        .select("article_key,image_url,image_details_url")
        .eq("slug", slug)
        .single();

      if (fetchError) {
        console.error("Error fetching article for deletion:", fetchError);
        return false;
      }

      const articleKey: string | undefined = articleData?.article_key;
      const imageUrl: string | undefined = articleData?.image_url;
      const imageDetailsUrl: string | undefined =
        articleData?.image_details_url;

      // 1) Delete i18n rows for this article_key
      if (articleKey) {
        const { error: i18nError } = await supabase
          .from("i18n")
          .delete()
          .eq("namespace", "articles")
          .like("key", `${articleKey}.%`);
        if (i18nError) {
          console.error("Error deleting i18n rows:", i18nError);
          // Continue anyway
        }
      }

      // 2) Delete images from storage if present
      const filePathsToRemove: string[] = [];
      const extractPath = (publicUrl?: string): string | null => {
        if (!publicUrl) return null;
        // Public URL format: .../storage/v1/object/public/<bucket>/<path>
        const marker = `/storage/v1/object/public/${STORAGE_BUCKETS.ARTICLE_IMAGES}/`;
        const idx = publicUrl.indexOf(marker);
        if (idx === -1) return null;
        const rawPath = publicUrl.substring(idx + marker.length);
        // Decode URL-encoded characters to match the exact storage object key
        try {
          return decodeURIComponent(rawPath);
        } catch {
          return rawPath;
        }
      };

      const imagePath = extractPath(imageUrl);
      const imageDetailsPath = extractPath(imageDetailsUrl);
      if (imagePath) filePathsToRemove.push(imagePath);
      if (imageDetailsPath) filePathsToRemove.push(imageDetailsPath);

      if (filePathsToRemove.length > 0) {
        // Remove duplicates just in case
        const uniquePaths = Array.from(new Set(filePathsToRemove));
        const { error: storageError } = await supabase.storage
          .from(STORAGE_BUCKETS.ARTICLE_IMAGES)
          .remove(uniquePaths);
        if (storageError) {
          console.error("Error deleting storage images:", storageError);
          // Continue anyway
        }
      }

      // 3) Delete article row
      const { error: deleteError } = await supabase
        .from(TABLES.ARTICLES)
        .delete()
        .eq("slug", slug);

      if (deleteError) {
        console.error("Error deleting article:", deleteError);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in deleteArticle:", error);
      return false;
    }
  }

  // Image Upload
  async uploadImage(file: File, fileName: string): Promise<string | null> {
    try {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKETS.ARTICLE_IMAGES)
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Error uploading image:", error);
        return null;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(STORAGE_BUCKETS.ARTICLE_IMAGES)
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error("Error in uploadImage:", error);
      return null;
    }
  }

  async deleteImage(fileName: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKETS.ARTICLE_IMAGES)
        .remove([fileName]);

      if (error) {
        console.error("Error deleting image:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in deleteImage:", error);
      return false;
    }
  }

  // Fix article key for existing articles that have conflicts
  async fixArticleKey(slug: string, newArticleKey: string): Promise<boolean> {
    try {
      // Update the article record
      const { error: articleError } = await supabase
        .from(TABLES.ARTICLES)
        .update({
          article_key: newArticleKey,
          title_key: `articles.${newArticleKey}.title`,
          meta_key: `articles.${newArticleKey}.meta`,
          content_title_key: `articles.${newArticleKey}.contentTitle`,
          content_key: `articles.${newArticleKey}.content`,
          tags_key: `articles.${newArticleKey}.tags`,
        })
        .eq("slug", slug);

      if (articleError) {
        console.error("Error updating article key:", articleError);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in fixArticleKey:", error);
      return false;
    }
  }

  // Data mapping helpers
  private mapDatabaseToArticle(dbArticle: any): Article {
    // Use article_key from database if it exists, otherwise extract from title_key
    let article_key = dbArticle.article_key;
    if (!article_key && dbArticle.title_key) {
      // Extract article key from title_key like "articles.article1.title"
      const match = dbArticle.title_key.match(/^articles\.([^.]+)\./);
      if (match) {
        article_key = match[1];
      }
    }

    return {
      slug: dbArticle.slug,
      image: dbArticle.image_url || "",
      imageDetails: dbArticle.image_details_url || "",
      titleKey: dbArticle.title_key || "",
      metaKey: dbArticle.meta_key || "",
      contentTitleKey: dbArticle.content_title_key || "",
      contentKey: dbArticle.content_key || "",
      tagsKey: dbArticle.tags_key || "",
      article_key: article_key,
    };
  }

  private mapArticleToDatabase(article: Article): Partial<DatabaseArticle> {
    return {
      slug: article.slug,
      title_key: article.titleKey,
      meta_key: article.metaKey,
      content_title_key: article.contentTitleKey,
      content_key: article.contentKey,
      tags_key: article.tagsKey,
      image_url: article.image,
      image_details_url: article.imageDetails,
    };
  }

  // Initialize database (run this once to set up tables)
  async initializeDatabase(): Promise<void> {
    try {
      // Create articles table if it doesn't exist
      const { error } = await supabase.rpc(
        "create_articles_table_if_not_exists"
      );
      if (error) {
        console.log(
          "Table creation error (this is normal if table exists):",
          error
        );
      }
    } catch (error) {
      console.error("Error initializing database:", error);
    }
  }
}

export const supabaseService = new SupabaseService();
